// Bidirectional sync: when the user selects a cell in Excel, find the snippet that was
// written there and reveal/highlight it in the viewer. Mirrors the original add-in's
// SheetSelectionChange → ScrollToSnippet behaviour.
//
// worksheet selection events need ExcelApi 1.7; if unsupported we just skip the feature
// (the manifest minimum stays at 1.4 so the add-in still loads on older hosts).
import { state, revealSnippet } from './store'

let started = false

export async function startSelectionSync(): Promise<void> {
  if (started) return
  if (typeof Excel === 'undefined' || typeof Office === 'undefined') return
  try {
    if (!Office.context?.requirements?.isSetSupported?.('ExcelApi', '1.7')) return
  } catch {
    return
  }
  started = true
  try {
    await Excel.run(async (ctx) => {
      ctx.workbook.worksheets.onSelectionChanged.add(onSelectionChanged)
      await ctx.sync()
    })
  } catch {
    started = false
  }
}

async function onSelectionChanged(): Promise<void> {
  try {
    await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet()
      sheet.load(['name', 'id'])
      const range = ctx.workbook.getSelectedRange()
      range.load('address')
      await ctx.sync()

      const address = range.address.includes('!') ? range.address.split('!')[1] : range.address
      const firstCell = address.split(':')[0]
      // Prefer the stable sheet id (rename-proof); fall back to the display name for older snippets.
      const match = state.snippets.find(
        (s) =>
          (s.worksheetId ? s.worksheetId === sheet.id : s.worksheetName === sheet.name) &&
          rangeContains(s.cellAddress, firstCell),
      )
      if (match) revealSnippet(match.id)
    })
  } catch {
    /* selection sync is best-effort */
  }
}

function colToNum(col: string): number {
  let n = 0
  for (const ch of col.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64)
  return n
}

function parseCell(a: string): { c: number; r: number } | null {
  const m = a.match(/^\$?([A-Za-z]+)\$?(\d+)$/)
  if (!m) return null
  return { c: colToNum(m[1]), r: Number.parseInt(m[2], 10) }
}

/** True if A1 `cellAddr` falls within `rangeAddr` (a single cell or "B7:D9"). */
function rangeContains(rangeAddr: string, cellAddr: string): boolean {
  const cell = parseCell(cellAddr)
  if (!cell) return false
  const [p0, p1] = rangeAddr.split(':')
  const a = parseCell(p0)
  const b = parseCell(p1 ?? p0)
  if (!a || !b) return false
  return (
    cell.c >= Math.min(a.c, b.c) &&
    cell.c <= Math.max(a.c, b.c) &&
    cell.r >= Math.min(a.r, b.r) &&
    cell.r <= Math.max(a.r, b.r)
  )
}
